import React from "react";
import { Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface LockedControlProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  locked: boolean;
  /** Hover copy explaining why the controls are disabled. */
  message: React.ReactNode;
  /** Render inline (`span`) instead of block. */
  inline?: boolean;
  side?: React.ComponentProps<typeof TooltipContent>["side"];
}

/**
 * Disables every form control inside it while `locked` and explains why on
 * hover. Renders children untouched when unlocked, so call sites can wrap
 * unconditionally.
 *
 * Disabled elements swallow pointer events, so the tooltip is attached to a
 * wrapper that receives them instead (children get `pointer-events: none`).
 */
const LockedControl: React.FC<LockedControlProps> = ({
  children,
  locked,
  message,
  inline = false,
  className,
  side = "top",
  ...wrapperProps
}) => {
  if (!locked) return <>{children}</>;

  const Wrapper = inline ? "span" : "div";

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Wrapper
            tabIndex={0}
            aria-disabled="true"
            {...wrapperProps}
            className={cn(
              "cursor-not-allowed rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring",
              inline ? "inline-block" : "block",
              className
            )}
          >
            <fieldset
              disabled
              className={cn(
                // Full width of the wrapper: a wrapper sized by its content is unchanged, and
                // one stretched by the caller (`w-full`) lets a full-width control fill it.
                "m-0 w-full min-w-0 border-0 p-0 opacity-60 [&_*]:pointer-events-none",
                inline ? "inline-block" : "block"
              )}
            >
              {children}
            </fieldset>
          </Wrapper>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          collisionPadding={8}
          className="max-w-xs text-xs leading-relaxed"
        >
          <div className="flex items-start gap-1.5">
            <Lock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
            <span>{message}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default LockedControl;
